/** @jsxImportSource @emotion/react */

import Typography from '@mui/material/Typography'
import arrayMutators from 'final-form-arrays'
import createDecorator from 'final-form-focus'
import { FC } from 'react'
import { Form as FinalForm } from 'react-final-form'
import { FieldArray } from 'react-final-form-arrays'
import { findAllQuestion_findAllQuestion_items as Question } from '__generatedTypes__/findAllQuestion'
import { findOneVideo_findOneVideo as IVideo } from '__generatedTypes__/findOneVideo'
import { findOneVideoCategory_findOneVideoCategory as IVideoCategory } from '__generatedTypes__/findOneVideoCategory'
import { QuestionLocationTypeEnum, SubjectEnumType } from '__generatedTypes__/globalTypes'
import DetailsBlock from 'components/DetailsBlock'
import { InputField } from 'components/Input'
import QuestionAccordion from 'components/QuestionAccordion'
import QuestionPicker from 'components/QuestionPicker'
import { PlatformSlug } from 'constants/consts'
import { CourseName } from 'theme/utils'
import { validateRequired } from 'utils/validation'
import * as commonClasses from '../commonStyles'
import { IQuizData } from '../types'
import { getAvailableQuestionTypes } from '../../QuestionBanks/utils'

interface IProps {
  platformSlug: PlatformSlug
  videoCategory: IVideoCategory
  initialValues?: Omit<IVideo, '__typename'>
  errorText?: string | null
  handleSubmit: (data: IQuizData) => void
  onDeleteClick?: () => void
}

const focusOnErrors = createDecorator<IVideo, Partial<IVideo>>()

const VideoQuizForm: FC<IProps> = ({
  platformSlug,
  videoCategory,
  initialValues,
  errorText,
  handleSubmit,
  onDeleteClick,
}) => (
  <div css={commonClasses.formContainer}>
    <Typography variant="title3" css={commonClasses.title}>
      {initialValues ? 'Edit Video Quiz' : 'Create Video Quiz'}
    </Typography>
    <FinalForm<IVideo>
      mutators={{ ...arrayMutators }}
      decorators={[focusOnErrors]}
      onSubmit={(values) => handleSubmit(values as IQuizData)}
      initialValues={initialValues}
      render={({ handleSubmit, initialValues, form }) => (
        <form onSubmit={handleSubmit} css={commonClasses.contentContainer}>
          <div css={commonClasses.block}>
            <InputField name="title" validate={validateRequired} label="Title" placeholder="Enter Title" fullWidth />
            <FieldArray<Question> name="questions" validate={validateRequired}>
              {({ fields, meta }) => (
                <>
                  <QuestionPicker
                    label="Questions"
                    value={fields.value ?? []}
                    onChange={(questions) => form.change('questions', questions)}
                    error={meta.touched && meta.error}
                    formHelperText={meta.touched && typeof meta.error === 'string' ? meta.error : undefined}
                    filter={{
                      platform: { id: videoCategory.subject.platform.id },
                      types: getAvailableQuestionTypes({ platformSlug }),
                    }}
                    willUsedIn={QuestionLocationTypeEnum.Video}
                    hideSelectedItems
                    fullWidth
                  />
                  <QuestionAccordion
                    questions={fields.value ?? []}
                    onSort={(questions) => form.change('questions', questions)}
                    onDeleteClick={(_, index) => fields.remove(index)}
                    onQuestionUpdated={(questionUpd, index) => fields.update(index, questionUpd)}
                  />
                </>
              )}
            </FieldArray>
          </div>
          <DetailsBlock
            title="Video Quiz Details"
            itemName="Video Quiz"
            tag={
              videoCategory.subject.type === SubjectEnumType.VideosOnly
                ? CourseName.CARS
                : (videoCategory.subject.tabName as CourseName)
            }
            tagLabel={videoCategory.subject.tabName}
            createDate={initialValues?.createdAt}
            statusLive={!!initialValues?.isLive}
            onDeleteClick={onDeleteClick}
            deleteDisabledReason={initialValues?.isLive ? "You can't delete Video Quiz because Live is enabled" : null}
            errorText={errorText}
          />
        </form>
      )}
    />
  </div>
)
export default VideoQuizForm
